import { Routes, Route, Navigate } from 'react-router-dom';
import AppShell from './components/AppShell';
import { useAuth } from './context/AuthContext';
import { lazy } from 'react';

import Landing from './pages/Landing';

// Role workspaces and shared pages are lazy-loaded so the initial bundle only
// contains what the entry route needs. Each page is fetched on first navigate,
// keeping the first paint fast without changing any UI or behaviour.
const ManagerWorkspace = lazy(() => import('./pages/ManagerWorkspace'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const ClientProject = lazy(() => import('./pages/ClientProject'));
const ClientNewProject = lazy(() => import('./pages/ClientNewProject'));
const ClientActivation = lazy(() => import('./pages/ClientActivation'));
const ClientNotifications = lazy(() => import('./pages/ClientNotifications'));
const ClientReports = lazy(() => import('./pages/ClientReports'));
const Profile = lazy(() => import('./pages/Profile'));
const AgentWorkspace = lazy(() => import('./pages/AgentWorkspace'));
const AgentProject = lazy(() => import('./pages/AgentProject'));
const AdminDashboard = lazy(() => import('./pages/Admin'));
// AdminProjects is a named export from ./pages/Admin; reshape to a default.
const AdminProjects = lazy(() =>
  import('./pages/Admin').then((mod) => ({ default: mod.AdminProjects })),
);
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const EntityList = lazy(() => import('./pages/EntityList'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));

function Splash() {
  return <div className="route-loading">Loading…</div>;
}

/**
 * Role guard.
 *
 * Mirrors the original per-page guard: a signed-out visitor goes to the auth
 * page, and an account that an administrator has not approved cannot proceed.
 */
function Guard({ role, children }) {
  const { session, profile, loading, signOut } = useAuth();

  if (loading) return <Splash />;
  if (!session) return <Navigate to="/" replace />;

  // A signed-in account with no profile row cannot be placed in a role, so it
  // must not slip through. This used to be skipped because the checks below
  // were gated on `profile &&`, which turned a failed profile fetch into a
  // bypass of both the approval gate and the role gate.
  if (!profile) {
    return (
      <div className="route-loading">
        <h1 className="page-title">Account unavailable</h1>
        <p className="page-subtitle">
          We could not load your Valdon profile. Sign out and try again, or ask an
          administrator to check your account.
        </p>
        <div className="page-head-actions">
          <button type="button" className="btn btn-secondary" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Approval only holds roles an administrator releases — site agents and
  // project managers. Clients are trusted from the moment they sign up; what
  // protects the client side is per-project activation, so a client only
  // stops here when an administrator has deactivated the account itself.
  const unapproved = profile.approval_status !== 'Approved' && profile.role !== 'client';
  if (profile.active === false || unapproved) {
    return (
      <div className="route-loading">
        <h1 className="page-title">{profile.active === false ? 'Account deactivated' : 'Awaiting approval'}</h1>
        <p className="page-subtitle">
          {profile.active === false
            ? 'This account has been deactivated. Contact Valdon Site Control 360 for help.'
            : 'This account is waiting for a Valdon administrator to review it.'}
        </p>
        <div className="page-head-actions">
          <button type="button" className="btn btn-secondary" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (role && profile.role !== role) {
    return <Navigate to={homeFor(profile.role)} replace />;
  }

  return <AppShell>{children}</AppShell>;
}

function homeFor(role) {
  switch (role) {
    case 'project_manager': return '/manager';
    case 'client': return '/client';
    case 'site_agent': return '/agent';
    case 'administrator': return '/admin';
    default: return '/';
  }
}

function Home() {
  const { session, profile, loading } = useAuth();
  if (loading) return <Splash />;
  if (!session) return <Landing />;
  return <Navigate to={homeFor(profile?.role)} replace />;
}

/** A page that exists in the original app but still needs porting. */
const pending = (title) => (
  <Guard>
    <ComingSoon title={title} />
  </Guard>
);

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Landing />} />

      {/* ── Project Manager ─────────────────────────────────────────── */}
      <Route path="/manager" element={<Guard role="project_manager"><ManagerWorkspace /></Guard>} />
      <Route path="/manager/projects" element={<Guard role="project_manager"><EntityList title="Projects" icon="assignment" breadcrumb={["Project Manager", "Projects"]} table="projects" columns="id, project_name, status, setup_status, created_at" order="created_at" /></Guard>} />
      <Route path="/manager/profile" element={<Guard role="project_manager"><Profile role="project_manager" /></Guard>} />

      {/* ── Client ──────────────────────────────────────────────────── */}
      <Route path="/client" element={<Guard role="client"><ClientDashboard /></Guard>} />
      <Route path="/client/new-project" element={<Guard role="client"><ClientNewProject /></Guard>} />
      <Route path="/client/project/:id" element={<Guard role="client"><ClientProject /></Guard>} />
      <Route path="/client/project/:id/:section" element={<Guard role="client"><ClientProject /></Guard>} />
      <Route path="/client/notifications" element={<Guard role="client"><ClientNotifications /></Guard>} />
      <Route path="/client/profile" element={<Guard role="client"><Profile role="client" /></Guard>} />

      {/* ── Site Agent ──────────────────────────────────────────────── */}
      <Route path="/agent" element={<Guard role="site_agent"><AgentWorkspace /></Guard>} />
      <Route path="/agent/project" element={<Guard role="site_agent"><AgentProject /></Guard>} />
      <Route path="/agent/reports" element={<Guard role="site_agent"><EntityList title="Daily Reports" icon="description" breadcrumb={["Site Agent", "Reports"]} table="reports" columns="id, report_date, status, progress_percent, work_completed" order="report_date" /></Guard>} />
      <Route path="/agent/photos" element={<Guard role="site_agent"><EntityList title="Site Photos" icon="photo_library" breadcrumb={["Site Agent", "Photos"]} table="report_photos" columns="id, caption, category, verification_status, uploaded_at" order="uploaded_at" /></Guard>} />
      <Route path="/agent/profile" element={<Guard role="site_agent"><Profile role="site_agent" /></Guard>} />

      {/* ── Administrator ───────────────────────────────────────────── */}
      <Route path="/admin" element={<Guard role="administrator"><AdminDashboard /></Guard>} />
      <Route path="/admin/projects" element={<Guard role="administrator"><AdminProjects /></Guard>} />
      <Route path="/admin/users" element={<Guard role="administrator"><AdminUsers /></Guard>} />
      <Route path="/admin/profile" element={<Guard role="administrator"><Profile role="administrator" /></Guard>} />

      {/* ── Reports (shared by manager + admin) ─────────────────────── */}
      <Route path="/reports/client" element={<Guard role="client"><ClientReports /></Guard>} />
      <Route path="/reports/admin" element={<Guard role="administrator"><AdminReports /></Guard>} />

      {/* ── Activation ──────────────────────────────────────────────── */}
      <Route path="/client/activation" element={<Guard role="client"><ClientActivation /></Guard>} />

      <Route path="*" element={pending("Not found")} />
    </Routes>
  );
}